import { computed, ref } from 'vue'
import {
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '@/services/notifications'

const notifications = ref([])
const unreadCount = ref(0)
const loading = ref(false)
const error = ref(null)
let loaded = false
let refreshPromise = null

function unwrap(value) {
  return value?.message ?? value ?? {}
}

export function useNotifications() {
  async function refresh(limit = 20) {
    if (refreshPromise) return refreshPromise

    refreshPromise = (async () => {
      loading.value = true
      error.value = null
      try {
        const result = unwrap(await getNotifications(limit))
        notifications.value = result.notifications || []
        unreadCount.value = Number(result.unread_count || 0)
        loaded = true
        return result
      } catch (cause) {
        error.value = cause
        throw cause
      } finally {
        loading.value = false
      }
    })()

    try {
      return await refreshPromise
    } finally {
      refreshPromise = null
    }
  }

  async function ensureLoaded() {
    if (loaded) return
    await refresh()
  }

  async function markRead(notification) {
    if (!notification || notification.is_read) return
    notification.is_read = 1
    notification.read_at = new Date().toISOString()
    unreadCount.value = Math.max(0, unreadCount.value - 1)
    try {
      await markNotificationRead(notification.name)
    } catch (cause) {
      notification.is_read = 0
      notification.read_at = null
      unreadCount.value += 1
      throw cause
    }
  }

  async function markAllRead() {
    await markAllNotificationsRead()
    notifications.value.forEach((notification) => {
      notification.is_read = 1
      notification.read_at ||= new Date().toISOString()
    })
    unreadCount.value = 0
  }

  return {
    notifications,
    unreadCount,
    loading,
    error,
    loaded: computed(() => loaded),
    refresh,
    ensureLoaded,
    markRead,
    markAllRead,
  }
}
